// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Team from "./team.js"
import User from "./user.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    },
    "user_id": {
      "column": {
        "default": null,
        "name": "user_id",
        "null": false,
        "type": null
      },
      "name": "user_id",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "TeamUser",
  "collectionKey": "team_users",
  "collectionName": "team_users",
  "i18nKey": "team_user",
  "camelizedLower": "teamUser",
  "name": "TeamUser",
  "nameDasherized": "team-user",
  "pluralName": "team_users",
  "ransackable_associations": [
    {
      "className": "TeamUser::Audit",
      "collectionName": "team_user_audits",
      "foreignKey": "team_user_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "TeamUserAudit",
      "through": null
    },
    {
      "className": "TeamUser::Audit",
      "collectionName": "team_user_audits",
      "foreignKey": "team_user_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "TeamUserAudit",
      "through": null
    },
    {
      "className": "Team",
      "collectionName": "teams",
      "foreignKey": "team_id",
      "name": "team",
      "macro": "belongs_to",
      "resource_name": "Team",
      "through": null
    },
    {
      "className": "User",
      "collectionName": "users",
      "foreignKey": "user_id",
      "name": "user",
      "macro": "belongs_to",
      "resource_name": "User",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "role",
      "column": {
        "default": "member",
        "name": "role",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "team_id",
      "column": {
        "default": null,
        "name": "team_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "user_id",
      "column": {
        "default": null,
        "name": "user_id",
        "null": false,
        "type": null
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "Team",
      "collectionName": "teams",
      "foreignKey": "team_id",
      "name": "team",
      "macro": "belongs_to",
      "resource_name": "Team",
      "through": null
    },
    {
      "className": "User",
      "collectionName": "users",
      "foreignKey": "user_id",
      "name": "user",
      "macro": "belongs_to",
      "resource_name": "User",
      "through": null
    }
  ],
  "paramKey": "team_user",
  "primaryKey": "id"
}

/** Frontend model for TeamUser. */
export default class TeamUser extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  userId() {
    return this.readAttributeUnderscore("user_id")
  }

  /** @returns {boolean} */
  hasUserId() {
    const value = this.userId()

    return this._isPresent(value)
  }

  /** @returns {import("./team.js").default | null} */
  team() {
    return this._readBelongsToReflection({modelClass: Team, reflectionName: "team"})
  }

  /** @returns {Promise<import("./team.js").default | null>} */
  loadTeam() {
    if (!("teamId" in this)) throw new Error("Foreign key method wasn't defined: teamId")

    const id = this.teamId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "team",
        model: this,
        modelClass: Team
      },
      {ransack}
    )
  }

  /** @returns {import("./user.js").default | null} */
  user() {
    return this._readBelongsToReflection({modelClass: User, reflectionName: "user"})
  }

  /** @returns {Promise<import("./user.js").default | null>} */
  loadUser() {
    if (!("userId" in this)) throw new Error("Foreign key method wasn't defined: userId")

    const id = this.userId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "user",
        model: this,
        modelClass: User
      },
      {ransack}
    )
  }
}
